import React, { useLayoutEffect, useRef, useState } from 'react'

import type {
  IdType,
  TitleTextElementState,
  TitleTextBubbleChunk,
} from 'EditorApp/types'

import TitleTextBubble from 'components/TitleTextBubble'
import FixedSizeOverflowWarning from 'components/FixedSizeOverflowWarning'
import CharacterLengthWarning from 'components/CharacterLengthWarning'
import TooManyLinesWarning from 'components/TooManyLinesWarning'
import useFontsLoaded from 'hooks/useFontsLoaded'
import { convertPercentToPx } from 'utils/unitHelpers'

export interface Props {
  fontFamily: string
  isActive: boolean
  onUpdateElement: (
    elementId: IdType,
    pageId: IdType,
    updates: Partial<Element>
  ) => void
  pageId: IdType
  zoom: number
  element: TitleTextElementState
}

const TitleTextElement = ({
  fontFamily,
  isActive,
  onUpdateElement = () => {},
  pageId,
  element,
  zoom = 1,
}: Props) => {
  const topBubbleRef = useRef<HTMLDivElement>(null)
  const bottomBubbleRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const fontsLoaded = useFontsLoaded()

  const [indentationPx, setIndentationPx] = useState(0)

  const handleChange = (elementAttrs: Partial<TitleTextElementState>) => {
    onUpdateElement(element.id, pageId, elementAttrs)
  }

  useLayoutEffect(() => {
    const topBubble = topBubbleRef.current
    if (!topBubble) return

    const topBubbleWidth = topBubble.getBoundingClientRect().width / zoom
    setIndentationPx(convertPercentToPx(topBubbleWidth, element.indentationPct))
  }, [
    isActive,
    fontsLoaded,
    element.fontSize,
    element.topBubbleChunks,
    element.indentationPct,
    zoom,
  ])

  const onTopBubbleChange = (chunks: TitleTextBubbleChunk[]) => {
    handleChange({ topBubbleChunks: chunks })
  }

  const onBottomBubbleChange = (chunks: TitleTextBubbleChunk[]) => {
    handleChange({ bottomBubbleChunks: chunks })
  }

  return (
    <div
      className="TitleTextElement"
      data-element-id={element.id}
      style={{
        height: element.height,
        width: element.width,
      }}
      ref={containerRef}
    >
      {isActive && (
        <>
          <CharacterLengthWarning
            chunks={element.topBubbleChunks.concat(element.bottomBubbleChunks)}
            minCharacters={6}
            maxCharacters={60}
          />
          <TooManyLinesWarning
            chunks={element.topBubbleChunks.concat(element.bottomBubbleChunks)}
            maxLines={4}
          />
          {/* TODO: remove once we have auto element resizing */}
          <FixedSizeOverflowWarning
            message="title text is too large for the bounding box"
            containerRef={containerRef}
            allContentRefs={[topBubbleRef, bottomBubbleRef]}
          />
        </>
      )}
      {isActive || !element.renderedSvg ? (
        <>
          <TitleTextBubble
            color={element.color}
            chunks={element.topBubbleChunks}
            indentPx={0}
            fontFamily={fontFamily}
            fontSize={element.fontSize}
            lineHeight={element.lineHeight}
            onChange={onTopBubbleChange}
            ref={topBubbleRef}
          />
          <TitleTextBubble
            color={element.altColor || element.color}
            chunks={element.bottomBubbleChunks}
            indentPx={indentationPx}
            fontFamily={fontFamily}
            fontSize={element.fontSize}
            lineHeight={element.lineHeight}
            onChange={onBottomBubbleChange}
            ref={bottomBubbleRef}
          />
        </>
      ) : (
        <div
          className="TitleTextElement__container"
          dangerouslySetInnerHTML={{ __html: element.renderedSvg }}
        />
      )}
    </div>
  )
}

export default TitleTextElement
